import { useMemo, useState } from 'react';
import { X, GitCompare, ArrowRight } from 'lucide-react';
import { useDeckSnapshots, type DeckSnapshot } from '@/hooks/useDeckSnapshots';
import { useCards } from '@/hooks/useCards';
import type { DeckCard } from '@/hooks/useDecks';

interface CompareSnapshotsSheetProps {
  open: boolean;
  onClose: () => void;
  deckId: string;
  currentCards: DeckCard[];
}

type DiffRow = {
  cardId: string;
  name: string;
  fromQty: number;
  toQty: number;
  delta: number;
};

function toMap(snap?: DeckSnapshot | null, current?: DeckCard[]): Record<string, number> {
  const m: Record<string, number> = {};
  if (snap) {
    for (const c of snap.snapshot_json.cards) m[c.card_id] = (m[c.card_id] || 0) + c.quantity;
  } else if (current) {
    for (const c of current) m[c.card_id] = (m[c.card_id] || 0) + c.quantity;
  }
  return m;
}

export function CompareSnapshotsSheet({
  open,
  onClose,
  deckId,
  currentCards,
}: CompareSnapshotsSheetProps) {
  const { data: snapshots } = useDeckSnapshots(open ? deckId : null);
  const { data: allCards } = useCards();

  const options = useMemo(() => {
    const opts: { id: string; label: string }[] = [{ id: 'current', label: 'Current deck' }];
    (snapshots || []).forEach((s) => {
      const when = new Date(s.created_at).toLocaleString(undefined, {
        dateStyle: 'short',
        timeStyle: 'short',
      });
      opts.push({ id: s.id, label: `${s.label} · ${when}` });
    });
    return opts;
  }, [snapshots]);

  const [fromId, setFromId] = useState<string>('');
  const [toId, setToId] = useState<string>('current');

  // Default "from" to the most recent snapshot once they load.
  if (!fromId && options.length > 1) setFromId(options[1].id);

  const fromSnap = snapshots?.find((s) => s.id === fromId) || null;
  const toSnap = snapshots?.find((s) => s.id === toId) || null;
  const fromMap = useMemo(
    () => toMap(fromSnap, fromId === 'current' ? currentCards : []),
    [fromSnap, fromId, currentCards],
  );
  const toMap_ = useMemo(
    () => toMap(toSnap, toId === 'current' ? currentCards : []),
    [toSnap, toId, currentCards],
  );

  const nameFor = (id: string) => allCards?.find((c) => c.id === id)?.name || id.slice(0, 8);

  const diff: DiffRow[] = useMemo(() => {
    const ids = new Set([...Object.keys(fromMap), ...Object.keys(toMap_)]);
    const rows: DiffRow[] = [];
    for (const id of ids) {
      const fromQty = fromMap[id] || 0;
      const toQty = toMap_[id] || 0;
      if (fromQty !== toQty) {
        rows.push({ cardId: id, name: nameFor(id), fromQty, toQty, delta: toQty - fromQty });
      }
    }
    return rows.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta) || a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromMap, toMap_, allCards]);

  if (!open) return null;

  const added = diff.filter((d) => d.delta > 0);
  const removed = diff.filter((d) => d.delta < 0);
  const fromTotal = Object.values(fromMap).reduce((s, n) => s + n, 0);
  const toTotal = Object.values(toMap_).reduce((s, n) => s + n, 0);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="animate-slide-up w-full max-w-[430px] max-h-[92vh] flex flex-col rounded-t-2xl border border-border bg-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <GitCompare className="h-4 w-4 text-primary" />
            <h3 className="font-display text-base font-semibold text-foreground">Compare snapshots</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 border-b border-border space-y-2">
          <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-body">
              From
              <select
                value={fromId}
                onChange={(e) => setFromId(e.target.value)}
                className="mt-1 w-full rounded-lg border border-border bg-secondary px-2 py-2 text-xs text-foreground font-body focus:outline-none"
              >
                {options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <ArrowRight className="h-4 w-4 text-muted-foreground mb-2.5" />
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-body">
              To
              <select
                value={toId}
                onChange={(e) => setToId(e.target.value)}
                className="mt-1 w-full rounded-lg border border-border bg-secondary px-2 py-2 text-xs text-foreground font-body focus:outline-none"
              >
                {options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="text-[10px] text-muted-foreground font-body text-center">
            {fromTotal} cards <span className="mx-1">→</span> {toTotal} cards
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {!diff.length ? (
            <div className="py-12 text-center">
              <p className="text-2xl mb-1.5">🟰</p>
              <p className="text-xs text-muted-foreground font-body">
                These two snapshots are identical.
              </p>
            </div>
          ) : (
            <>
              {added.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-ink-emerald font-body mb-1.5 px-1">
                    Added ({added.reduce((s, r) => s + r.delta, 0)})
                  </p>
                  <div className="space-y-1">
                    {added.map((r) => (
                      <DiffLine key={`a-${r.cardId}`} row={r} />
                    ))}
                  </div>
                </div>
              )}
              {removed.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-ink-ruby font-body mb-1.5 px-1">
                    Removed ({removed.reduce((s, r) => s + Math.abs(r.delta), 0)})
                  </p>
                  <div className="space-y-1">
                    {removed.map((r) => (
                      <DiffLine key={`r-${r.cardId}`} row={r} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DiffLine({ row }: { row: DiffRow }) {
  const positive = row.delta > 0;
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-secondary/40 px-3 py-2">
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-foreground font-body truncate">{row.name}</p>
        <p className="text-[10px] text-muted-foreground font-body">
          {row.fromQty} → {row.toQty}
        </p>
      </div>
      <span
        className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium font-body ${
          positive ? 'bg-ink-emerald/15 text-ink-emerald' : 'bg-ink-ruby/15 text-ink-ruby'
        }`}
      >
        {positive ? '+' : ''}
        {row.delta}
      </span>
    </div>
  );
}
